import { Collaboration } from '@pncl/common-models';
import { matchI } from 'ts-adt';
import { PencilSocket } from '../socket-utilities/pencil-socket';
import EventProvider = Collaboration.EventProvider;
import ADTUtil = Collaboration.Utils.ADTUtil;

type Callback = (...args: unknown[]) => void;

interface EventOptions {
  volatile: boolean;
}

export class ClientEventProvider {
  private readonly _handlers: Record<string, Callback[] | undefined> = {};
  private readonly _subscribedScopes: Record<string, EventProvider.Scope> = {};

  constructor(private socket: PencilSocket<string>) {
    this.socket.on(EventProvider.EventName, (_res) => {
      const res = _res as EventProvider.Response;
      matchI(res)({
        Subscribe: () => {
          throw new Error('Unexpected Subscribe');
        },
        Unsubscribe: () => {
          throw new Error('Unexpected Unsubscribe');
        },
        Event: (event) => this._handleEvent(event),
      });
    });

    this.socket.on('connect', () => {
      const previouslySubscribedScopes = Object.values(this._subscribedScopes);
      previouslySubscribedScopes.forEach((scope) => this.subscribe(scope));
    });
  }

  public _handleEvent(event: ADTUtil.ExtractADT<EventProvider.Response, 'Event'>): void {
    const eventName = EventProvider._getRoomNameForScope(event.scope);
    this._handlers[eventName]?.forEach((cb) => cb(event.data));
  }

  public sendEvent(
    event: ADTUtil.ExtractADT<EventProvider.Request, 'Event'>,
    options?: EventOptions,
  ): void {
    if (options?.volatile) {
      this.socket.volatile();
    }
    this._sendMessageToServer(event);
  }

  // Do this so we can implement the event emitter API correctly
  public eventNameForScope(scope: EventProvider.Scope): string {
    return EventProvider._getRoomNameForScope(scope);
  }

  public on(eventName: string, callback: Callback): this {
    if (!this._handlers[eventName]) {
      this._handlers[eventName] = [callback];
    } else {
      this._handlers[eventName]?.push(callback);
    }
    return this;
  }

  public off(eventName: string, cb: Callback): void {
    this._handlers[eventName] = this._handlers[eventName]?.filter((_cb) => _cb !== cb);
  }

  // Note: caller should include auth when calling this
  public async subscribe(scope: EventProvider.Scope): Promise<boolean> {
    const subscribe: ADTUtil.ExtractADT<EventProvider.Request, 'Subscribe'> = {
      _type: 'Subscribe',
      scope,
    };

    return new Promise((resolve) => {
      this._sendMessageToServer(subscribe, (res) => {
        if (res._type === 'Subscribe' && res.Response._type === 'Success') {
          this._subscribedScopes[EventProvider._getRoomNameForScope(scope)] = scope;
          resolve(true);
        }
        resolve(false);
      });
    });
  }

  public async unsubscribe(scope: EventProvider.Scope): Promise<boolean> {
    const unsubscribe: ADTUtil.ExtractADT<EventProvider.Request, 'Unsubscribe'> = {
      _type: 'Unsubscribe',
      scope,
    };

    return new Promise((resolve) => {
      this._sendMessageToServer(unsubscribe, (res) => {
        if (res._type === 'Unsubscribe' && res.Response._type === 'Success') {
          delete this._subscribedScopes[EventProvider._getRoomNameForScope(scope)];
          resolve(true);
        }
        resolve(false);
      });
    });
  }

  private _sendMessageToServer(
    req: EventProvider.Request,
    cb?: (res: EventProvider.Response) => void,
  ) {
    if (cb) {
      this.socket.emit(EventProvider.EventName, req, cb);
    } else {
      this.socket.emit(EventProvider.EventName, req);
    }
  }
}
